import React, { useCallback, useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import Pagination from "../../../components/Pagination";
import { refinePages } from "../../../utils/paginationUtils";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

// 운영진용 오류 신고 게시판. 전체 학원의 신고를 한자리에서 보고, 상태를 바꾸고,
// 답변을 적는다. 서버는 이미 있었고 화면만 없었다.
//
// **답변은 학원 화면에 그대로 나간다.** 처리완료로 넘기는 순간 그 학원의 첫
// 화면에 "수정했습니다" 팝업이 한 번 뜨고, 누르면 이 답변이 붙은 글로 간다.
// 그래서 여기 적는 글은 우리끼리 보는 메모가 아니다.
const STATUS_LIST = [
  { value: "received", text: "접수" },
  { value: "working", text: "확인 중" },
  { value: "done", text: "처리완료" },
  { value: "hold", text: "보류" },
];

const STATUS_COLOR = {
  received: "#E0863C",
  working: "#186a96",
  done: "#00A396",
  hold: "#8a949c",
};

const FILTER_LIST = [{ value: "", text: "전체" }, ...STATUS_LIST];

// 한 쪽에 세 건. 사진과 답변 칸이 붙어서 한 건이 길다.
const PAGE_SIZE = 3;

const AdminErrorReportPage = () => {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [draftMap, setDraftMap] = useState({});
  const [savingId, setSavingId] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const isMobile = Math.min(window.innerWidth, window.screen.width) <= 768;

  const load = useCallback(() => {
    apiClient
      .get(`/api/v1/support/admin/reports?status=${statusFilter}`, {
        withAuth: true,
      })
      .then((response) => {
        const loadedRows = response.data.rows || [];
        setRows(loadedRows);
        // 적다 만 답변을 목록을 새로 받을 때마다 날리지 않는다. 없는 것만 채운다.
        setDraftMap((previous) => {
          const merged = { ...previous };
          loadedRows.forEach((row) => {
            if (merged[row.id] === undefined) merged[row.id] = row.reply || "";
          });
          return merged;
        });
      })
      .catch(() => {});
  }, [statusFilter]);

  useEffect(load, [load]);

  // 다른 상태로 걸러 보면 첫 쪽부터 본다. 안 그러면 세 건뿐인데 3쪽에 남아
  // 빈 화면이 뜬다.
  useEffect(() => setCurrentPage(1), [statusFilter]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const save = async (report, nextStatus) => {
    setSavingId(report.id);
    try {
      await apiClient.post(
        "/api/v1/support/admin/reports",
        {
          id: report.id,
          status: nextStatus || report.status,
          reply: draftMap[report.id] ?? "",
        },
        { withAuth: true }
      );
      load();
    } catch (error) {
      window.alert(error?.response?.data?.error || "저장하지 못했습니다.");
    }
    setSavingId(0);
  };

  return (
    <div
      style={{
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
        padding: isMobile ? "16px 14px 40px" : "24px 28px 60px",
      }}
    >
      <div className="section-title" style={{ marginBottom: "12px" }}>
        오류 신고 (전체 학원)
        <span
          style={{
            marginLeft: "8px",
            fontSize: "13px",
            fontWeight: 500,
            color: "#8a949c",
          }}
        >
          {rows.length}건
        </span>
      </div>

      <div style={{ display: "flex", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
        {FILTER_LIST.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            style={{
              border: "1px solid #D3DBE0",
              background: statusFilter === filter.value ? "#0E141B" : "#fff",
              color: statusFilter === filter.value ? "#fff" : "#3A434E",
              fontWeight: 700,
              fontSize: "13px",
              padding: "7px 14px",
              cursor: "pointer",
            }}
          >
            {filter.text}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          style={{
            border: "1px solid #D3DBE0",
            background: "#fff",
            color: "#8a949c",
            fontSize: "13px",
            padding: "7px 14px",
            cursor: "pointer",
            marginLeft: "auto",
          }}
        >
          새로고침
        </button>
      </div>

      {rows.length === 0 && (
        <div style={{ color: "#8a949c", fontSize: "14px" }}>신고가 없습니다.</div>
      )}

      <div style={{ display: "grid", gap: "10px" }}>
        {pageRows.map((row) => (
          <div
            key={row.id}
            style={{
              border: "1px solid #E3EBEA",
              background: "#fff",
              padding: "14px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 800,
                  color: "#fff",
                  background: STATUS_COLOR[row.status] || "#8a949c",
                  padding: "3px 9px",
                }}
              >
                {row.status_text}
              </span>
              <span style={{ fontSize: "14px", fontWeight: 800, color: "#0E141B" }}>
                {row.academy}
              </span>
              <span style={{ fontSize: "13px", color: "#8a949c" }}>
                {row.nickname} · {String(row.created_at).slice(0, 10)} · #{row.id}
              </span>
              {row.is_result_unseen && (
                <span style={{ fontSize: "12px", color: "#E0863C", fontWeight: 700 }}>
                  아직 안 봄
                </span>
              )}
            </div>

            <div style={{ fontSize: "14px", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
              {row.text}
            </div>

            {row.photo_url_list.length > 0 && (
              <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
                {row.photo_url_list.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer">
                    <img
                      src={url}
                      alt="첨부"
                      style={{
                        width: "62px",
                        height: "62px",
                        objectFit: "cover",
                        border: "1px solid #E3EBEA",
                      }}
                    />
                  </a>
                ))}
              </div>
            )}

            <textarea
              value={draftMap[row.id] ?? ""}
              onChange={(event) =>
                setDraftMap({ ...draftMap, [row.id]: event.target.value })
              }
              placeholder="학원에게 그대로 보이는 답변입니다. 무엇을 어떻게 했는지 한두 줄로 적습니다."
              style={{
                width: "100%",
                minHeight: "70px",
                marginTop: "12px",
                padding: "11px 12px",
                border: "1px solid #D3DBE0",
                fontSize: "14px",
                lineHeight: 1.7,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />

            <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
              {STATUS_LIST.map((statusItem) => (
                <button
                  key={statusItem.value}
                  type="button"
                  disabled={savingId === row.id}
                  onClick={() => save(row, statusItem.value)}
                  style={{
                    border: "1px solid #D3DBE0",
                    background: row.status === statusItem.value ? "#EFF5F5" : "#fff",
                    color: "#3A434E",
                    fontSize: "13px",
                    fontWeight: 700,
                    padding: "7px 13px",
                    cursor: savingId === row.id ? "default" : "pointer",
                  }}
                >
                  {statusItem.text}로
                </button>
              ))}
              <button
                type="button"
                disabled={savingId === row.id}
                onClick={() => save(row, row.status)}
                style={{
                  border: "none",
                  background: "#00A396",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 800,
                  padding: "7px 16px",
                  cursor: savingId === row.id ? "default" : "pointer",
                  marginLeft: "auto",
                }}
              >
                답변만 저장
              </button>
            </div>
          </div>
        ))}
      </div>

      <Pagination
        visiblePages={refinePages({
          total_pages: totalPages,
          current_page: currentPage,
        })}
        current_page={currentPage}
        handlePageClick={setCurrentPage}
      />
    </div>
  );
};

export default AdminErrorReportPage;
